import {
	ChevronDown,
	FileDown,
	FilePlus2,
	FileStack,
	ListTree,
	Plus,
	Printer,
	TriangleAlert,
	Workflow,
} from "lucide-react";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import type { StageKind } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ViewToggle, type ViewToggleOption } from "@/components/view-toggle";
import { BulletinDialog } from "@/features/measurements/components/bulletin-dialog";
import { ApplyTemplateDialog } from "@/features/stage-templates/components/apply-template-dialog";
import { SaveAsTemplateDialog } from "@/features/stage-templates/components/save-as-template-dialog";
import { StagesDiagram } from "@/features/stages/components/stages-diagram";
import { StagesLock } from "@/features/stages/components/stages-lock";
import {
	StagesTable,
	StagesTableSkeleton,
} from "@/features/stages/components/stages-table";
import { useActiveObra } from "@/features/stages/hooks/use-active-obra.ts";
import { useStages } from "@/features/stages/hooks/use-stages.ts";
import { usePermissions } from "@/hooks/use-permissions.ts";

type StagesView = "tabela" | "diagrama";

const VIEW_OPTIONS: ViewToggleOption<StagesView>[] = [
	{ value: "tabela", label: "Tabela", icon: ListTree },
	{ value: "diagrama", label: "Diagrama", icon: Workflow },
];

function stagesSummary(count: number): string {
	return `${count} ${count === 1 ? "etapa" : "etapas"}`;
}

function unvaluedNotice(count: number): string {
	const items = count === 1 ? "item sem valor" : "itens sem valor";
	return `${count} ${items} — a medição usa apenas percentuais`;
}

export function EtapasPage() {
	const { obra: obraKey } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const { has } = usePermissions();
	const {
		obra,
		isLoading: obraLoading,
		isError: obraError,
		refetch: refetchObra,
	} = useActiveObra();
	const stagesQuery = useStages(obra?.id);

	const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
	const [applyTemplateOpen, setApplyTemplateOpen] = useState(false);
	const [bulletinOpen, setBulletinOpen] = useState(false);
	const lock = stagesQuery.data?.lock;
	const totals = stagesQuery.data?.totals;
	const locked = !!lock?.locked;
	const canCreateTemplate = has("stage_templates:create");
	const canApplyTemplate = has("stage_templates:apply") && !locked;
	const canManageTemplates = canCreateTemplate || canApplyTemplate;
	const canViewValues = has("stage_values:view");
	const canPrintBulletin = has("stage_measurements:list");
	const valued = canViewValues && !!totals?.valued;
	const showUnvaluedNotice =
		canViewValues &&
		!!totals &&
		totals.unvaluedLeaves > 0 &&
		totals.totalCents > 0;

	const view: StagesView =
		searchParams.get("view") === "diagrama" ? "diagrama" : "tabela";

	function setView(next: StagesView) {
		setSearchParams(
			(params) => {
				if (next === "diagrama") {
					params.set("view", "diagrama");
				} else {
					params.delete("view");
				}
				return params;
			},
			{ replace: true },
		);
	}

	const stages = stagesQuery.data?.stages ?? [];
	const newStagePath = (parentId?: string, kind?: StageKind) => {
		const params = new URLSearchParams();
		if (parentId) params.set("parent", parentId);
		if (kind) params.set("kind", kind);
		const query = params.toString();
		return `/${obraKey}/etapas/adicionar${query ? `?${query}` : ""}`;
	};
	const stagePath = (stageId: string) => `/${obraKey}/etapas/${stageId}`;

	const isLoading = obraLoading || (!!obra && stagesQuery.isPending);

	return (
		<div className="stages-page">
			<div className="page-header">
				<div>
					<h1 className="page-title">Etapas da Obra</h1>
					<p className="page-subtitle">
						{obra
							? stagesSummary(stages.length)
							: "Gerencie as etapas e sub-etapas da obra."}
					</p>
				</div>
				<div className="page-header__actions page-header__actions--lg">
					{obra && (
						<ViewToggle
							options={VIEW_OPTIONS}
							value={view}
							onChange={setView}
						/>
					)}
					{obra && canPrintBulletin && (
						<Button variant="outline" onClick={() => setBulletinOpen(true)}>
							<Printer />
							Boletim de medição
						</Button>
					)}
					{obra && canManageTemplates && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="outline">
									<FileStack />
									Templates
									<ChevronDown />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								{canApplyTemplate && (
									<DropdownMenuItem onClick={() => setApplyTemplateOpen(true)}>
										<FileDown />
										Aplicar template
									</DropdownMenuItem>
								)}
								{canCreateTemplate && (
									<DropdownMenuItem onClick={() => setSaveTemplateOpen(true)}>
										<FilePlus2 />
										Salvar como template
									</DropdownMenuItem>
								)}
							</DropdownMenuContent>
						</DropdownMenu>
					)}
					{obra && has("construction_stages:create") && !locked && (
						<Button asChild>
							<Link to={newStagePath()}>
								<Plus />
								Nova Etapa
							</Link>
						</Button>
					)}
				</div>
			</div>

			{obra && lock && <StagesLock constructionId={obra.id} lock={lock} />}

			{showUnvaluedNotice && (
				<div className="pending-notice">
					<TriangleAlert className="pending-notice__icon" aria-hidden="true" />
					<p className="pending-notice__text">
						{unvaluedNotice(totals.unvaluedLeaves)}
					</p>
				</div>
			)}

			{obraError ? (
				<Card>
					<CardContent className="card-state">
						<p>Não foi possível carregar a obra.</p>
						<Button variant="outline" onClick={() => refetchObra()}>
							Tentar novamente
						</Button>
					</CardContent>
				</Card>
			) : isLoading || !obra ? (
				<StagesTableSkeleton />
			) : stagesQuery.isError ? (
				<Card>
					<CardContent className="card-state">
						<p>Não foi possível carregar as etapas.</p>
						<Button variant="outline" onClick={() => stagesQuery.refetch()}>
							Tentar novamente
						</Button>
					</CardContent>
				</Card>
			) : view === "tabela" ? (
				<StagesTable
					constructionId={obra.id}
					stages={stages}
					locked={locked}
					valued={valued}
					stagePath={stagePath}
					newStagePath={newStagePath}
				/>
			) : (
				<StagesDiagram
					constructionId={obra.id}
					stages={stages}
					locked={locked}
					stagePath={stagePath}
				/>
			)}

			{obra && canPrintBulletin && (
				<BulletinDialog
					open={bulletinOpen}
					onOpenChange={setBulletinOpen}
					constructionId={obra.id}
					showMoney={canViewValues}
				/>
			)}
			{obra && canCreateTemplate && (
				<SaveAsTemplateDialog
					open={saveTemplateOpen}
					onOpenChange={setSaveTemplateOpen}
					constructionId={obra.id}
					constructionName={obra.name}
				/>
			)}
			{obra && canApplyTemplate && (
				<ApplyTemplateDialog
					open={applyTemplateOpen}
					onOpenChange={setApplyTemplateOpen}
					constructionId={obra.id}
					templatesPath={`/${obraKey}/configuracoes/templates-de-etapas`}
				/>
			)}
		</div>
	);
}
